import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  ConfigProvider,
  Descriptions,
  Drawer,
  Empty,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tabs,
  Tag,
  Typography,
  Upload,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { UploadFile } from 'antd/es/upload';
import {
  CloudUploadOutlined,
  EyeOutlined,
  ImportOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import AdminLayout from '../components/AdminLayout';
import '../styles/AdminPages.css';
import '../styles/CukcukPages.css';
import viVN from 'antd/locale/vi_VN';
import { CUKCUK_PREVIEW_MAX_ROWS } from '../constants/cukcuk';
import type { CukcukParseResult } from '../types/cukcukImport';
import { parseCukcukMenu } from '../utils/cukcukParser';
import {
  parseCukcukInvoicesWithOptionMapping,
  type ParseCukcukInvoicesWithMappingResult,
} from '../utils/cukcukInvoiceImportUtils';
import { assertCukcukFileKind, validateCukcukUploadFile } from '../utils/cukcukFileValidation';
import { buildFullOptionCatalog } from '../utils/cukcukCatalogLoader';
import { computeFileFingerprint } from '../utils/fileFingerprint';
import { downloadWarningsCsv } from '../utils/cukcukWarningsExport';
import {
  cukcukCustomizationService,
  cukcukDishService,
  cukcukInvoiceNotificationService,
} from '../services/cukcukDataServices';
import {
  importInvoicesFromCukcuk,
  importMenuFromCukcuk,
  type CukcukInvoiceImportResult,
  type CukcukMenuImportResult,
} from '../services/cukcukImportService';
import { CukcukImportHistoryService } from '../services/cukcukImportHistoryService';
import type { InvoiceNotification } from '../types/invoiceNotification';
import type { Dish } from '../types/dish';

const { Title, Text, Paragraph } = Typography;

interface MenuPreviewRow {
  key: string;
  kind: 'Món' | 'Option' | 'Quan hệ';
  code: string;
  name: string;
  group: string;
  price: number;
  detail: string;
  source?: Partial<Dish> | Record<string, unknown>;
}

interface InvoicePreviewRow {
  key: string;
  invoiceCode: string;
  itemId: string;
  itemName: string;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
  options: string;
  mappedCodes: string;
  tableName: string;
  saleDate: string;
  mappedCount: number;
  optionCount: number;
  minMatchScore: number;
  source?: InvoiceNotification | Record<string, unknown>;
}

const formatPrice = (value: number) =>
  value > 0 ? `${value.toLocaleString('vi-VN')} ₫` : '—';

const buildMenuPreviewRows = (parsed: CukcukParseResult): MenuPreviewRow[] => {
  const rows: MenuPreviewRow[] = [];

  parsed.dishes.forEach((entry, index) => {
    rows.push({
      key: `dish-${entry.dish.code}-${index}`,
      kind: 'Món',
      code: entry.dish.code || '',
      name: entry.dish.name || '',
      group: entry.dish.groupName || entry.dish.group || '',
      price: entry.dish.price ?? 0,
      detail: entry.dish.unit || '',
    });
  });

  parsed.options.forEach((option, index) => {
    rows.push({
      key: `opt-${option.code}-${index}`,
      kind: 'Option',
      code: option.code,
      name: option.name,
      group: option.optionGroupName || '',
      price: option.price,
      detail: option.isActive ? 'Đang bán' : 'Ẩn',
    });
  });

  parsed.dishOptions.forEach((relation, index) => {
    rows.push({
      key: `rel-${relation.dishCode}-${relation.groupName}-${index}`,
      kind: 'Quan hệ',
      code: relation.dishCode,
      name: relation.dishName || relation.dishCode,
      group: relation.groupName,
      price: 0,
      detail: relation.options.map((opt) => opt.name).join(', '),
    });
  });

  return rows;
};

/** Hiển thị menu đã lưu trong Firebase (cukcuk_dishes) khi chưa chọn file. */
const buildSavedMenuPreviewRows = (
  dishes: Array<{
    code?: string;
    name?: string;
    groupName?: string;
    group?: string;
    price?: number;
    unit?: string;
    isVirtualDish?: boolean;
    isActive?: boolean;
  }>,
): MenuPreviewRow[] =>
  dishes
    .filter((dish) => !!dish.code?.trim() && !!dish.name?.trim())
    .map((dish, index) => ({
      key: `saved-${dish.code}-${index}`,
      kind: dish.isVirtualDish ? ('Option' as const) : ('Món' as const),
      code: dish.code!.trim(),
      name: dish.name!.trim(),
      group: (dish.groupName || dish.group || '').trim(),
      price: dish.price ?? 0,
      detail: dish.isVirtualDish
        ? dish.isActive === false
          ? 'Option · Ẩn'
          : 'Option · Đang bán'
        : dish.unit || 'Món đã lưu',
      source: dish,
    }))
    .sort((a, b) => {
      if (a.kind !== b.kind) return a.kind === 'Món' ? -1 : 1;
      return a.name.localeCompare(b.name, 'vi');
    });

const buildSavedInvoiceRows = (notifications: InvoiceNotification[]): InvoicePreviewRow[] =>
  notifications.map((invoice, index) => ({
    key: invoice.id || invoice.lineKey || `${invoice.invoiceCode}-${invoice.itemId}-${index}`,
    invoiceCode: invoice.invoiceCode,
    itemId: invoice.itemId || '—',
    itemName: invoice.itemName || '—',
    quantity: invoice.quantity || 0,
    unitPrice: invoice.unitPrice || 0,
    lineTotal: invoice.lineTotal || 0,
    options: invoice.optionSummary || invoice.selectedOptions?.map((option) => option.optionName).join(', ') || '—',
    mappedCodes: invoice.selectedOptions?.map((option) => option.optionId || '—').join(', ') || '—',
    tableName: invoice.tableName || '—',
    saleDate: invoice.saleDate || invoice.checkOutDate || '—',
    mappedCount: invoice.selectedOptions?.filter((option) => option.optionId).length || 0,
    optionCount: invoice.selectedOptions?.length || 0,
    minMatchScore: 0,
    source: invoice,
  }));

const buildInvoicePreviewRows = (parsed: ParseCukcukInvoicesWithMappingResult): InvoicePreviewRow[] => {
  const rows: InvoicePreviewRow[] = [];

  parsed.invoices.forEach((invoice) => {
    invoice.items.forEach((item, itemIndex) => {
      const optionCount = item.selectedOptions.length;
      const mappedCount = item.selectedOptions.filter((opt) => opt.mappedOption).length;
      const mappedScores = item.selectedOptions
        .map((opt) => opt.matchScore ?? 0)
        .filter((score) => score > 0);
      rows.push({
        key: `${invoice.invoiceCode}-${item.itemId}-${itemIndex}`,
        invoiceCode: invoice.invoiceCode,
        itemId: item.itemId,
        itemName: item.itemName,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        lineTotal: item.lineTotal,
        options: item.selectedOptions.map((opt) => opt.optionName).join(', ') || '—',
        mappedCodes:
          item.selectedOptions
            .map((opt) => opt.mappedOption?.code || '—')
            .join(', ') || '—',
        tableName: invoice.tableName || '—',
        saleDate: invoice.saleDate || '—',
        mappedCount,
        optionCount,
        minMatchScore: mappedScores.length ? Math.min(...mappedScores) : 0,
      });
    });
  });

  return rows;
};

const kindTagColor: Record<MenuPreviewRow['kind'], string> = {
  Món: 'blue',
  Option: 'purple',
  'Quan hệ': 'gold',
};

const buildMenuColumns = (
  kindFilters: Array<{ text: string; value: string }>,
  groupFilters: Array<{ text: string; value: string }>,
): ColumnsType<MenuPreviewRow> => [
  {
    title: 'Loại',
    dataIndex: 'kind',
    width: 110,
    filters: kindFilters,
    onFilter: (value, record) => record.kind === value,
    render: (kind: MenuPreviewRow['kind']) => <Tag color={kindTagColor[kind]}>{kind}</Tag>,
  },
  {
    title: 'Mã',
    dataIndex: 'code',
    width: 160,
    sorter: (a, b) => a.code.localeCompare(b.code, 'vi'),
  },
  {
    title: 'Tên',
    dataIndex: 'name',
    ellipsis: true,
    sorter: (a, b) => a.name.localeCompare(b.name, 'vi'),
  },
  {
    title: 'Nhóm',
    dataIndex: 'group',
    width: 160,
    ellipsis: true,
    filters: groupFilters,
    onFilter: (value, record) => (record.group || '') === value,
    sorter: (a, b) => (a.group || '').localeCompare(b.group || '', 'vi'),
  },
  {
    title: 'Giá',
    dataIndex: 'price',
    width: 110,
    align: 'right',
    sorter: (a, b) => a.price - b.price,
    render: (price: number) => formatPrice(price),
  },
  { title: 'Chi tiết', dataIndex: 'detail', ellipsis: true },
];

const invoiceColumns: ColumnsType<InvoicePreviewRow> = [
  { title: 'Số HĐ', dataIndex: 'invoiceCode', width: 120 },
  { title: 'Mã món', dataIndex: 'itemId', width: 110 },
  { title: 'Tên món', dataIndex: 'itemName', ellipsis: true },
  { title: 'SL', dataIndex: 'quantity', width: 60, align: 'center' },
  {
    title: 'Đơn giá',
    dataIndex: 'unitPrice',
    width: 100,
    align: 'right',
    render: (v: number) => formatPrice(v),
  },
  {
    title: 'Thành tiền',
    dataIndex: 'lineTotal',
    width: 110,
    align: 'right',
    render: (v: number) => formatPrice(v),
  },
  { title: 'Tùy chọn', dataIndex: 'options', ellipsis: true },
  { title: 'Mã đã map', dataIndex: 'mappedCodes', width: 160, ellipsis: true },
  {
    title: 'Map OK',
    dataIndex: 'mappedCount',
    width: 80,
    align: 'center',
    render: (count: number, record) => {
      if (!record.optionCount) return '—';
      return (
        <Tag color={count === record.optionCount ? 'success' : count > 0 ? 'warning' : 'error'}>
          {count}/{record.optionCount}
        </Tag>
      );
    },
  },
  {
    title: 'Score',
    dataIndex: 'minMatchScore',
    width: 70,
    align: 'center',
    render: (score: number, record) => {
      if (!record.optionCount) return '—';
      const color = score >= 900 ? 'success' : score >= 600 ? 'warning' : 'error';
      return <Tag color={color}>{score || '—'}</Tag>;
    },
  },
  { title: 'Bàn', dataIndex: 'tableName', width: 80 },
  { title: 'Ngày', dataIndex: 'saleDate', width: 100 },
];

const parseSaleDate = (value: string): number | null => {
  const parts = value.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
  if (parts) return new Date(Number(parts[3]), Number(parts[2]) - 1, Number(parts[1])).getTime();
  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? null : timestamp;
};

const capPreviewRows = <T,>(rows: T[]): { rows: T[]; truncated: number } => {
  if (rows.length <= CUKCUK_PREVIEW_MAX_ROWS) {
    return { rows, truncated: 0 };
  }
  return { rows: rows.slice(0, CUKCUK_PREVIEW_MAX_ROWS), truncated: rows.length - CUKCUK_PREVIEW_MAX_ROWS };
};

const collectUnmappedOptionNames = (parsed: ParseCukcukInvoicesWithMappingResult): string[] => {
  const seen = new Set<string>();
  const names: string[] = [];

  parsed.invoices.forEach((invoice) => {
    invoice.items.forEach((item) => {
      item.selectedOptions.forEach((option) => {
        if (option.mappedOption || !option.optionName.trim()) return;
        const key = option.optionName.trim().toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        names.push(option.optionName.trim());
      });
    });
  });

  return names;
};

interface ImportResultPanelProps {
  title: string;
  successCount: number;
  failCount: number;
  detail?: React.ReactNode;
  warnings?: string[];
}

const ImportResultPanel: React.FC<ImportResultPanelProps> = ({
  title,
  successCount,
  failCount,
  detail,
  warnings = [],
}) => (
  <Card size="small" style={{ marginTop: 16, background: '#fafafa' }}>
    <Title level={5} style={{ marginTop: 0 }}>
      {title}
    </Title>
    <Row gutter={24}>
      <Col>
        <Statistic title="Thành công" value={successCount} valueStyle={{ color: '#3f8600' }} />
      </Col>
      <Col>
        <Statistic title="Thất bại" value={failCount} valueStyle={{ color: failCount > 0 ? '#cf1322' : undefined }} />
      </Col>
    </Row>
    {detail}
    {warnings.length > 0 && (
      <Alert
        type="warning"
        showIcon
        style={{ marginTop: 12 }}
        message={`${warnings.length} cảnh báo`}
        description={
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {warnings.slice(0, 5).map((w, i) => (
              <li key={i}>{w}</li>
            ))}
            {warnings.length > 5 ? <li>... và {warnings.length - 5} cảnh báo khác</li> : null}
          </ul>
        }
      />
    )}
  </Card>
);

/** Chỉ đọc/ghi collection CUKCUK — không dùng 8am_* / invoice_notifications (iPOS). */
const customizationService = cukcukCustomizationService;
const dishService = cukcukDishService;
const importHistoryService = new CukcukImportHistoryService();

const confirmDuplicateImport = (
  previous: { fileName: string; importedAt: Date },
  onConfirm: () => void,
): void => {
  Modal.confirm({
    title: 'File đã được import trước đó',
    content: (
      <div>
        <p>
          File này trùng fingerprint với lần import{' '}
          <strong>{formatImportedAt(previous.importedAt)}</strong> ({previous.fileName}).
        </p>
        <p>Tiếp tục sẽ cập nhật dữ liệu hiện có (upsert), không tạo bản ghi trùng.</p>
      </div>
    ),
    okText: 'Tiếp tục import',
    cancelText: 'Hủy',
    onOk: onConfirm,
  });
};

const confirmAutoCreateOptions = (optionNames: string[], onConfirm: () => void): void => {
  Modal.confirm({
    title: 'Tự tạo option chưa có trong catalog?',
    width: 640,
    content: (
      <div>
        <p>
          Có <strong>{optionNames.length}</strong> tùy chọn chưa map. Hệ thống sẽ tạo món ảo (giá 0) và gán
          vào customization món cha.
        </p>
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {optionNames.slice(0, 8).map((name) => (
            <li key={name}>{name}</li>
          ))}
          {optionNames.length > 8 ? <li>... và {optionNames.length - 8} tùy chọn khác</li> : null}
        </ul>
      </div>
    ),
    okText: 'Tạo và lưu',
    cancelText: 'Hủy',
    onOk: onConfirm,
  });
};

const formatImportedAt = (value: Date) =>
  value.toLocaleString('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const detailLabels: Record<string, string> = {
  id: 'ID Firebase', code: 'Mã món', name: 'Tên món', group: 'Nhóm món', groupName: 'Nhóm món',
  type: 'Loại món', typeName: 'Loại món', price: 'Giá bán', unit: 'Đơn vị tính', vat: 'Thuế VAT',
  city: 'Khu vực', description: 'Mô tả', isActive: 'Đang kinh doanh', isVirtualDish: 'Món tùy chọn',
  preparationTime: 'Thời gian chế biến', createdAt: 'Ngày tạo', updatedAt: 'Ngày cập nhật',
  invoiceCode: 'Mã hóa đơn', billNumber: 'Số chứng từ', itemId: 'Mã món', itemName: 'Tên món',
  quantity: 'Số lượng', unitPrice: 'Đơn giá', lineTotal: 'Thành tiền', tableName: 'Bàn', saleDate: 'Ngày bán',
  employeeName: 'Nhân viên', customerName: 'Khách hàng', customerPhone: 'Số điện thoại',
  paymentMethodName: 'Phương thức thanh toán', selectedOptions: 'Tùy chọn', optionSummary: 'Tóm tắt tùy chọn',
  notificationTitle: 'Tiêu đề thông báo', notificationContent: 'Nội dung thông báo', status: 'Trạng thái',
  channel: 'Kênh bán hàng', note: 'Ghi chú', priceConfiguration: 'Thiết lập giá',
};

const hiddenDetailKeys = new Set(['rawData', 'group', 'typeName']);
const moneyDetailKeys = new Set(['price', 'unitPrice', 'lineTotal', 'totalAmount', 'amount']);

const detailValue = (key: string, value: unknown): React.ReactNode => {
  if (value === null || value === undefined || value === '') return '—';
  if (value instanceof Date) return formatImportedAt(value);
  if (moneyDetailKeys.has(key) && typeof value === 'number') return `${value.toLocaleString('vi-VN')} ₫`;
  if (key === 'vat' && typeof value === 'number') return `${value}%`;
  if (key === 'preparationTime' && typeof value === 'number') return `${value} phút`;
  if (typeof value === 'boolean') return value ? 'Có' : 'Không';
  if (key === 'priceConfiguration' && typeof value === 'object') {
    const config = value as Record<string, boolean>;
    return (
      <Space direction="vertical" size={2}>
        <Text>Cho sửa giá khi bán: <strong>{config.allowPriceEdit ? 'Có' : 'Không'}</strong></Text>
        <Text>Yêu cầu số lượng: <strong>{config.requireQuantity ? 'Có' : 'Không'}</strong></Text>
        <Text>Cho xóa không cần quyền: <strong>{config.allowRemoveWithoutPermission ? 'Có' : 'Không'}</strong></Text>
      </Space>
    );
  }
  if (typeof value === 'object' && 'seconds' in (value as object)) {
    return new Date(Number((value as { seconds: number }).seconds) * 1000).toLocaleString('vi-VN');
  }
  if (Array.isArray(value)) return value.length ? value.map((item) => typeof item === 'object' ? Object.values(item).filter(Boolean).join(' · ') : String(item)).join(', ') : '—';
  if (typeof value === 'object') return <pre className="cukcuk-detail-json">{JSON.stringify(value, null, 2)}</pre>;
  return String(value);
};

interface CukcukImportPageProps {
  view?: 'menu' | 'invoice';
}

const CukcukImportPage: React.FC<CukcukImportPageProps> = ({ view = 'menu' }) => {
  const [importOpen, setImportOpen] = useState(false);
  const [detailRecord, setDetailRecord] = useState<{ title: string; data: Record<string, unknown> } | null>(null);

  const [menuFile, setMenuFile] = useState<File | null>(null);
  const [menuFileList, setMenuFileList] = useState<UploadFile[]>([]);
  const [menuParsed, setMenuParsed] = useState<CukcukParseResult | null>(null);
  const [menuPreviewLoading, setMenuPreviewLoading] = useState(false);
  const [menuSaveLoading, setMenuSaveLoading] = useState(false);
  const [menuImportResult, setMenuImportResult] = useState<CukcukMenuImportResult | null>(null);
  const [savedMenuRows, setSavedMenuRows] = useState<MenuPreviewRow[]>([]);
  const [savedMenuLoading, setSavedMenuLoading] = useState(false);
  const [menuSearch, setMenuSearch] = useState('');
  const [menuKindFilter, setMenuKindFilter] = useState<'all' | MenuPreviewRow['kind']>('all');
  const [menuGroupFilter, setMenuGroupFilter] = useState<string>('all');
  const menuPreviewRequestIdRef = useRef(0);

  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
  const [invoiceFileList, setInvoiceFileList] = useState<UploadFile[]>([]);
  const [invoiceParsed, setInvoiceParsed] = useState<ParseCukcukInvoicesWithMappingResult | null>(null);
  const [invoicePreviewLoading, setInvoicePreviewLoading] = useState(false);
  const [invoiceSaveLoading, setInvoiceSaveLoading] = useState(false);
  const [invoiceImportResult, setInvoiceImportResult] = useState<CukcukInvoiceImportResult | null>(null);
  const [invoiceWarnings, setInvoiceWarnings] = useState<string[]>([]);
  const [savedInvoiceRows, setSavedInvoiceRows] = useState<InvoicePreviewRow[]>([]);
  const [savedInvoiceLoading, setSavedInvoiceLoading] = useState(false);
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [invoiceMapFilter, setInvoiceMapFilter] = useState<'all' | 'mapped' | 'partial' | 'unmapped'>('all');
  const [invoiceTableFilter, setInvoiceTableFilter] = useState('all');
  const [invoiceDateFrom, setInvoiceDateFrom] = useState('');
  const [invoiceDateTo, setInvoiceDateTo] = useState('');

  const loadSavedMenu = useCallback(async () => {
    setSavedMenuLoading(true);
    try {
      const dishes = await dishService.getAllDishes();
      setSavedMenuRows(buildSavedMenuPreviewRows(dishes));
    } catch {
      setSavedMenuRows([]);
      message.warning('Không tải được menu CUKCUK đã lưu trên Firebase.');
    } finally {
      setSavedMenuLoading(false);
    }
  }, []);

  const loadSavedInvoices = useCallback(async () => {
    setSavedInvoiceLoading(true);
    try {
      const invoices = await cukcukInvoiceNotificationService.getAllNotifications();
      setSavedInvoiceRows(buildSavedInvoiceRows(invoices));
    } catch {
      setSavedInvoiceRows([]);
      message.warning('Không tải được hóa đơn CUKCUK đã lưu trên Firebase.');
    } finally {
      setSavedInvoiceLoading(false);
    }
  }, []);

  useEffect(() => {
    if (view === 'menu') void loadSavedMenu();
    if (view === 'invoice') void loadSavedInvoices();
  }, [loadSavedInvoices, loadSavedMenu, view]);

  // Ưu tiên xem trước file upload; không có file thì hiện menu đã lưu (full list)
  const menuAllRows = useMemo(
    () => (menuParsed ? buildMenuPreviewRows(menuParsed) : savedMenuRows),
    [menuParsed, savedMenuRows],
  );

  const menuKindFilterOptions = useMemo(() => {
    const kinds = Array.from(new Set(menuAllRows.map((row) => row.kind)));
    return kinds.map((kind) => ({ text: kind, value: kind }));
  }, [menuAllRows]);

  const menuGroupFilterOptions = useMemo(() => {
    const groups = Array.from(
      new Set(menuAllRows.map((row) => (row.group || '').trim()).filter(Boolean)),
    ).sort((a, b) => a.localeCompare(b, 'vi'));
    return groups.map((group) => ({ text: group, value: group }));
  }, [menuAllRows]);

  const menuColumns = useMemo(
    () => [
      ...buildMenuColumns(menuKindFilterOptions, menuGroupFilterOptions),
      {
        title: '',
        key: 'action',
        width: 90,
        fixed: 'right' as const,
        render: (_: unknown, record: MenuPreviewRow) => (
          <Button
            type="link"
            aria-label={`Chi tiết món ${record.code}`}
            onClick={() => setDetailRecord({ title: record.name, data: (record.source || record) as Record<string, unknown> })}
          >
            Chi tiết
          </Button>
        ),
      },
    ],
    [menuKindFilterOptions, menuGroupFilterOptions],
  );

  const invoiceTableColumns = useMemo<ColumnsType<InvoicePreviewRow>>(
    () => [
      ...invoiceColumns,
      {
        title: '',
        key: 'action',
        width: 90,
        fixed: 'right',
        render: (_: unknown, record) => (
          <Button
            type="link"
            aria-label={`Chi tiết hóa đơn ${record.invoiceCode}`}
            onClick={() => setDetailRecord({ title: `Hóa đơn ${record.invoiceCode}`, data: (record.source || record) as Record<string, unknown> })}
          >
            Chi tiết
          </Button>
        ),
      },
    ],
    [],
  );

  const filteredMenuRows = useMemo(() => {
    const keyword = menuSearch.trim().toLowerCase();
    return menuAllRows.filter((row) => {
      if (menuKindFilter !== 'all' && row.kind !== menuKindFilter) return false;
      if (menuGroupFilter !== 'all' && (row.group || '') !== menuGroupFilter) return false;
      if (keyword) {
        const haystack = `${row.code} ${row.name} ${row.group} ${row.detail}`.toLowerCase();
        if (!haystack.includes(keyword)) return false;
      }
      return true;
    });
  }, [menuAllRows, menuGroupFilter, menuKindFilter, menuSearch]);

  const menuTableSource: 'file' | 'saved' | 'empty' = menuParsed
    ? 'file'
    : savedMenuRows.length > 0
      ? 'saved'
      : 'empty';

  const invoiceAllRows = useMemo(
    () => (invoiceParsed ? buildInvoicePreviewRows(invoiceParsed) : savedInvoiceRows),
    [invoiceParsed, savedInvoiceRows],
  );

  const invoiceTableOptions = useMemo(
    () => Array.from(new Set(invoiceAllRows.map((row) => row.tableName).filter((name) => name !== '—')))
      .sort((a, b) => a.localeCompare(b, 'vi')),
    [invoiceAllRows],
  );

  const filteredInvoiceRows = useMemo(() => {
    const keyword = invoiceSearch.trim().toLowerCase();
    return invoiceAllRows.filter((row) => {
      if (keyword && !`${row.invoiceCode} ${row.itemId} ${row.itemName} ${row.tableName} ${row.saleDate}`.toLowerCase().includes(keyword)) return false;
      if (invoiceTableFilter !== 'all' && row.tableName !== invoiceTableFilter) return false;
      if (invoiceMapFilter === 'mapped' && (!row.optionCount || row.mappedCount !== row.optionCount)) return false;
      if (invoiceMapFilter === 'partial' && !(row.mappedCount > 0 && row.mappedCount < row.optionCount)) return false;
      if (invoiceMapFilter === 'unmapped' && !(row.optionCount > 0 && row.mappedCount === 0)) return false;
      const saleTimestamp = parseSaleDate(row.saleDate);
      if ((invoiceDateFrom || invoiceDateTo) && saleTimestamp === null) return false;
      if (invoiceDateFrom && saleTimestamp! < new Date(`${invoiceDateFrom}T00:00:00`).getTime()) return false;
      if (invoiceDateTo && saleTimestamp! > new Date(`${invoiceDateTo}T23:59:59`).getTime()) return false;
      return true;
    });
  }, [invoiceAllRows, invoiceDateFrom, invoiceDateTo, invoiceMapFilter, invoiceSearch, invoiceTableFilter]);

  const invoicePreview = useMemo(() => capPreviewRows(filteredInvoiceRows), [filteredInvoiceRows]);

  const resetMenu = useCallback(() => {
    setMenuFile(null);
    setMenuFileList([]);
    setMenuParsed(null);
    setMenuImportResult(null);
  }, []);

  const resetInvoice = useCallback(() => {
    setInvoiceFile(null);
    setInvoiceFileList([]);
    setInvoiceParsed(null);
    setInvoiceImportResult(null);
    setInvoiceWarnings([]);
  }, []);

  const handleMenuPreview = useCallback(
    async (fileOverride?: File | null, options?: { silent?: boolean }) => {
      const file = fileOverride ?? menuFile;
      if (!file) {
        if (!options?.silent) {
          message.warning('Vui lòng chọn file Excel trước.');
        }
        return;
      }

      const requestId = ++menuPreviewRequestIdRef.current;
      setMenuPreviewLoading(true);
      setMenuImportResult(null);
      try {
        const validation = validateCukcukUploadFile(file, 'menu');
        if (!validation.valid) {
          message.error(validation.error);
          return;
        }

        const kindCheck = await assertCukcukFileKind(file, 'menu');
        if (!kindCheck.valid) {
          message.error(kindCheck.error);
          return;
        }

        const parsed = await parseCukcukMenu(file);
        if (requestId !== menuPreviewRequestIdRef.current) return;

        setMenuParsed(parsed);
        if (parsed.meta.warnings.length) {
          message.warning(`Parse xong với ${parsed.meta.warnings.length} cảnh báo.`);
        } else {
          message.success(`Đã parse ${parsed.dishes.length} món, ${parsed.options.length} option.`);
        }
      } catch (error) {
        if (requestId !== menuPreviewRequestIdRef.current) return;
        message.error(error instanceof Error ? error.message : 'Không thể xem trước file menu.');
        setMenuParsed(null);
      } finally {
        if (requestId === menuPreviewRequestIdRef.current) {
          setMenuPreviewLoading(false);
        }
      }
    },
    [menuFile],
  );

  const handleMenuSave = useCallback(async () => {
    if (!menuParsed) {
      message.warning('Hãy bấm "Xem trước" trước khi lưu.');
      return;
    }

    const persistMenu = async () => {
      setMenuSaveLoading(true);
      try {
        const fileHash = menuFile ? await computeFileFingerprint(menuFile) : undefined;
        const result = await importMenuFromCukcuk(menuParsed, {
          fileName: menuFile?.name,
          fileHash,
          fileSize: menuFile?.size,
        });
        setMenuImportResult(result);

        const success =
          result.dishes.added +
          result.dishes.updated +
          result.options.added +
          result.options.updated +
          result.relations.added +
          result.relations.updated;
        const fail = result.errors.length;

        if (fail === 0) {
          message.success(`Lưu thành công ${success} bản ghi.`);
        } else {
          message.warning(`Lưu xong: ${success} thành công, ${fail} lỗi.`);
        }
        // Làm mới danh sách menu đã lưu trên Firebase
        void loadSavedMenu();
      } catch (error) {
        message.error(error instanceof Error ? error.message : 'Không thể lưu menu.');
      } finally {
        setMenuSaveLoading(false);
      }
    };

    if (menuFile) {
      const fileHash = await computeFileFingerprint(menuFile);
      const previous = await importHistoryService.findByFileHash(fileHash, 'menu');
      if (previous) {
        confirmDuplicateImport(previous, () => void persistMenu());
        return;
      }
    }

    await persistMenu();
  }, [loadSavedMenu, menuFile, menuParsed]);

  const handleInvoicePreview = useCallback(async () => {
    if (!invoiceFile) {
      message.warning('Vui lòng chọn file Excel trước.');
      return;
    }

    setInvoicePreviewLoading(true);
    setInvoiceImportResult(null);
    try {
      const validation = validateCukcukUploadFile(invoiceFile, 'invoice');
      if (!validation.valid) {
        message.error(validation.error);
        return;
      }

      const kindCheck = await assertCukcukFileKind(invoiceFile, 'invoice');
      if (!kindCheck.valid) {
        message.error(kindCheck.error);
        return;
      }

      const [customizations, dishes] = await Promise.all([
        customizationService.getAllCustomizations(),
        dishService.getAllDishes(),
      ]);
      const catalog = buildFullOptionCatalog(customizations, dishes);
      const parsed = await parseCukcukInvoicesWithOptionMapping(invoiceFile, catalog);
      setInvoiceParsed(parsed);
      setInvoiceWarnings(parsed.meta.warnings);
      message.success(
        `Đã parse ${parsed.meta.totalInvoices} hóa đơn, ${parsed.meta.totalLineItems} dòng món.`,
      );
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Không thể xem trước file hóa đơn.');
      setInvoiceParsed(null);
      setInvoiceWarnings([]);
    } finally {
      setInvoicePreviewLoading(false);
    }
  }, [invoiceFile]);

  const handleInvoiceSave = useCallback(async () => {
    if (!invoiceParsed) {
      message.warning('Hãy bấm "Xem trước" trước khi lưu.');
      return;
    }

    const persistInvoice = async (allowAutoCreateOptions = true) => {
      setInvoiceSaveLoading(true);
      try {
        const fileHash = invoiceFile ? await computeFileFingerprint(invoiceFile) : undefined;
        const result = await importInvoicesFromCukcuk(invoiceParsed, {
          fileName: invoiceFile?.name,
          fileHash,
          fileSize: invoiceFile?.size,
          allowAutoCreateOptions,
        });
        setInvoiceImportResult(result);
        void loadSavedInvoices();

        const success = result.added + result.updated;
        const fail = result.errors.length;

        if (fail === 0) {
          message.success(`Lưu thành công ${success} dòng món (${result.options} tùy chọn).`);
        } else {
          message.warning(`Lưu xong: ${success} thành công, ${fail} lỗi.`);
        }
      } catch (error) {
        message.error(error instanceof Error ? error.message : 'Không thể lưu hóa đơn.');
      } finally {
        setInvoiceSaveLoading(false);
      }
    };

    const unmappedNames = collectUnmappedOptionNames(invoiceParsed);
    const runSave = () => {
      if (unmappedNames.length) {
        confirmAutoCreateOptions(unmappedNames, () => void persistInvoice(true));
        return;
      }
      void persistInvoice(true);
    };

    if (invoiceFile) {
      const fileHash = await computeFileFingerprint(invoiceFile);
      const previous = await importHistoryService.findByFileHash(fileHash, 'invoice');
      if (previous) {
        confirmDuplicateImport(previous, runSave);
        return;
      }
    }

    runSave();
  }, [invoiceFile, invoiceParsed, loadSavedInvoices]);

  const menuSuccessCount = menuImportResult
    ? menuImportResult.dishes.added +
      menuImportResult.dishes.updated +
      menuImportResult.options.added +
      menuImportResult.options.updated +
      menuImportResult.relations.added +
      menuImportResult.relations.updated
    : 0;

  const menuFailCount = menuImportResult?.errors.length ?? 0;

  const invoiceSuccessCount = invoiceImportResult
    ? invoiceImportResult.added + invoiceImportResult.updated
    : 0;

  const invoiceFailCount = invoiceImportResult?.errors.length ?? 0;

  const renderToolbar = (
    fileList: UploadFile[],
    onFileChange: (file: File | null, list: UploadFile[]) => void,
    onPreview: () => void,
    onSave: () => void,
    onReset: () => void,
    previewLoading: boolean,
    saveLoading: boolean,
    canPreview: boolean,
    canSave: boolean,
    acceptHint: string,
  ) => (
    <Space wrap size="middle" style={{ marginBottom: 16 }}>
      <Upload
        accept=".xls,.xlsx"
        maxCount={1}
        fileList={fileList}
        beforeUpload={(file) => {
          onFileChange(file as File, [
            {
              uid: file.uid,
              name: file.name,
              status: 'done',
            },
          ]);
          return false;
        }}
        onRemove={() => {
          onFileChange(null, []);
          return true;
        }}
      >
        <Button icon={<CloudUploadOutlined />}>Chọn file Excel</Button>
      </Upload>

      <Button
        icon={<EyeOutlined />}
        onClick={onPreview}
        loading={previewLoading}
        disabled={!canPreview}
      >
        Xem trước
      </Button>

      <Button
        type="primary"
        icon={<ImportOutlined />}
        onClick={onSave}
        loading={saveLoading}
        disabled={!canSave}
      >
        Lưu vào hệ thống
      </Button>

      <Button icon={<ReloadOutlined />} onClick={onReset}>
        Làm mới
      </Button>

      <Text type="secondary">{acceptHint}</Text>
    </Space>
  );

  return (
    <ConfigProvider locale={viVN}>
      <AdminLayout>
        <div className="cukcuk-page-header">
          <div>
            <Text className="cukcuk-eyebrow">DỮ LIỆU ĐỒNG BỘ CUKCUK</Text>
            <Title level={2} className="cukcuk-page-title">
              {view === 'menu' ? 'Menu CUKCUK' : 'Hóa đơn CUKCUK'}
            </Title>
            <Text type="secondary">
              {view === 'menu'
                ? 'Tra cứu món, option và nhóm món đã đồng bộ.'
                : 'Kiểm tra hóa đơn và trạng thái ánh xạ tùy chọn.'}
            </Text>
          </div>
          <Button type="primary" size="large" icon={<CloudUploadOutlined />} onClick={() => setImportOpen(true)}>
            Nhập {view === 'menu' ? 'menu' : 'hóa đơn'} từ Excel
          </Button>
        </div>

        <Modal
          title={`Nhập ${view === 'menu' ? 'menu' : 'hóa đơn'} từ CUKCUK`}
          open={importOpen}
          onCancel={() => setImportOpen(false)}
          footer={null}
          width={720}
        >
          <Paragraph type="secondary">
            Chọn file Excel xuất từ CUKCUK, xem trước dữ liệu rồi lưu vào hệ thống.
          </Paragraph>
          {importOpen && (view === 'menu'
            ? renderToolbar(menuFileList, (file, list) => {
                setMenuFile(file);
                setMenuFileList(list);
                setMenuParsed(null);
                setMenuImportResult(null);
                if (file) void handleMenuPreview(file, { silent: true });
              }, () => void handleMenuPreview(), handleMenuSave, resetMenu, menuPreviewLoading, menuSaveLoading, !!menuFile, !!menuParsed, 'Sheet "Thực đơn" · .xls / .xlsx')
            : renderToolbar(invoiceFileList, (file, list) => {
                setInvoiceFile(file);
                setInvoiceFileList(list);
                setInvoiceParsed(null);
                setInvoiceImportResult(null);
                setInvoiceWarnings([]);
              }, handleInvoicePreview, handleInvoiceSave, resetInvoice, invoicePreviewLoading, invoiceSaveLoading, !!invoiceFile, !!invoiceParsed, 'Chi tiết doanh thu · .xls / .xlsx'))}
        </Modal>

        <Drawer
          title={detailRecord?.title}
          open={!!detailRecord}
          onClose={() => setDetailRecord(null)}
          width={560}
        >
          {detailRecord && (
            <>
              <div className="cukcuk-detail-hero">
                <Text type="secondary">{view === 'menu' ? 'Món đã lưu' : 'Hóa đơn đã lưu'}</Text>
                <Title level={3}>{detailRecord.title}</Title>
              </div>
              <Tabs
                className="cukcuk-detail-tabs"
                items={[
                  {
                    key: 'summary',
                    label: 'Tổng quan',
                    children: (
                      <Descriptions column={1} bordered size="small" className="cukcuk-detail-list">
                        {Object.entries(detailRecord.data)
                          .filter(([key, value]) => !hiddenDetailKeys.has(key) && key !== 'selectedOptions' && value !== undefined && value !== null && value !== '')
                          .filter(([key]) => !(key === 'groupName' && detailRecord.data.group === detailRecord.data.groupName))
                          .filter(([key]) => !(key === 'type' && detailRecord.data.typeName === detailRecord.data.type))
                          .map(([key, value]) => (
                            <Descriptions.Item key={key} label={detailLabels[key] || key}>{detailValue(key, value)}</Descriptions.Item>
                          ))}
                      </Descriptions>
                    ),
                  },
                  {
                    key: 'options',
                    label: 'Tùy chọn',
                    children: detailRecord.data.selectedOptions ? (
                      <div className="cukcuk-option-list">
                        {(detailRecord.data.selectedOptions as Array<Record<string, unknown>>).map((option, index) => (
                          <div className="cukcuk-option-card" key={String(option.optionId || option.optionName || index)}>
                            <strong>{String(option.optionName || `Tùy chọn ${index + 1}`)}</strong>
                            <Text type="secondary">{option.optionId ? `Mã: ${option.optionId}` : 'Chưa ánh xạ mã'}</Text>
                          </div>
                        ))}
                      </div>
                    ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Không có tùy chọn" />,
                  },
                  ...(detailRecord.data.rawData ? [{
                    key: 'excel',
                    label: 'Dữ liệu Excel',
                    children: (
                      <section className="cukcuk-excel-section">
                        <Text className="cukcuk-detail-kicker">DỮ LIỆU NGUỒN</Text>
                        <Title level={5}>Thông tin từ Excel</Title>
                        <Descriptions column={1} bordered size="small" className="cukcuk-detail-list">
                          {Object.entries(detailRecord.data.rawData as Record<string, unknown>)
                            .filter(([, value]) => value !== undefined && value !== null && value !== '')
                            .map(([key, value]) => {
                              const savedValue = detailRecord.data[key];
                              const differs = savedValue !== undefined && String(savedValue) !== String(value);
                              return (
                                <Descriptions.Item key={key} label={detailLabels[key] || key} className={differs ? 'cukcuk-detail-different' : ''}>
                                  {detailValue(key, value)}
                                  {differs && <Tag color="warning" className="cukcuk-difference-tag">Khác dữ liệu đã lưu</Tag>}
                                </Descriptions.Item>
                              );
                            })}
                        </Descriptions>
                      </section>
                    ),
                  }] : []),
                ]}
              />
            </>
          )}
        </Drawer>

        <div className="cukcuk-content-card">
          <Tabs
            activeKey={view}
            renderTabBar={() => <></>}
            items={[
              {
                key: 'menu',
                label: 'Import Menu',
                children: (
                  <>
                    <div className="cukcuk-summary-bar">
                      <div className="cukcuk-summary-item">
                        <Text type="secondary">Tổng dữ liệu</Text>
                        <strong>{menuAllRows.length}</strong>
                      </div>
                      <div className="cukcuk-summary-item">
                        <Text type="secondary">Món</Text>
                        <strong>{menuAllRows.filter((row) => row.kind === 'Món').length}</strong>
                      </div>
                      <div className="cukcuk-summary-item">
                        <Text type="secondary">Tùy chọn</Text>
                        <strong>{menuAllRows.filter((row) => row.kind === 'Option').length}</strong>
                      </div>
                      <Button type="text" icon={<ReloadOutlined />} onClick={() => void loadSavedMenu()} loading={savedMenuLoading}>
                        Làm mới
                      </Button>
                    </div>

                    <Card size="small" style={{ marginBottom: 12 }}>
                      <Row gutter={[12, 12]} align="middle">
                        <Col xs={24} md={10}>
                          <Input
                            allowClear
                            prefix={<SearchOutlined />}
                            placeholder="Tìm theo mã, tên, nhóm…"
                            value={menuSearch}
                            onChange={(e) => setMenuSearch(e.target.value)}
                          />
                        </Col>
                        <Col xs={12} md={5}>
                          <Select
                            style={{ width: '100%' }}
                            value={menuKindFilter}
                            onChange={(value) => setMenuKindFilter(value)}
                            options={[
                              { value: 'all', label: 'Tất cả loại' },
                              ...menuKindFilterOptions.map((item) => ({
                                value: item.value,
                                label: item.text,
                              })),
                            ]}
                          />
                        </Col>
                        <Col xs={12} md={6}>
                          <Select
                            style={{ width: '100%' }}
                            showSearch
                            optionFilterProp="label"
                            value={menuGroupFilter}
                            onChange={(value) => setMenuGroupFilter(value)}
                            options={[
                              { value: 'all', label: 'Tất cả nhóm' },
                              ...menuGroupFilterOptions.map((item) => ({
                                value: item.value,
                                label: item.text,
                              })),
                            ]}
                          />
                        </Col>
                        <Col xs={24} md={3} style={{ textAlign: 'right' }}>
                          <Button
                            onClick={() => {
                              setMenuSearch('');
                              setMenuKindFilter('all');
                              setMenuGroupFilter('all');
                            }}
                          >
                            Xóa lọc
                          </Button>
                        </Col>
                      </Row>
                      <div style={{ marginTop: 8, color: '#8c8c8c', fontSize: 12 }}>
                        Hiển thị <strong>{filteredMenuRows.length}</strong> / {menuAllRows.length} dòng
                        {menuSearch || menuKindFilter !== 'all' || menuGroupFilter !== 'all'
                          ? ' (đã lọc)'
                          : ''}
                      </div>
                    </Card>

                    <Table<MenuPreviewRow>
                      columns={menuColumns}
                      dataSource={filteredMenuRows}
                      loading={menuPreviewLoading || (menuTableSource !== 'file' && savedMenuLoading)}
                      size="small"
                      scroll={{ x: 900 }}
                      pagination={{
                        pageSize: 15,
                        showSizeChanger: true,
                        showTotal: (t) => `${t} dòng`,
                      }}
                      locale={{
                        emptyText: menuPreviewLoading
                          ? 'Đang đọc file…'
                          : filteredMenuRows.length === 0 && menuAllRows.length > 0
                            ? 'Không có dòng khớp bộ lọc'
                            : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Chưa có menu CUKCUK"><Button type="primary" onClick={() => setImportOpen(true)}>Nhập menu</Button></Empty>,
                      }}
                    />

                    {menuImportResult && (
                      <ImportResultPanel
                        title="Kết quả lưu Menu"
                        successCount={menuSuccessCount}
                        failCount={menuFailCount}
                        detail={
                          <div style={{ marginTop: 12 }}>
                            <Text>
                              Món: +{menuImportResult.dishes.added} / ~{menuImportResult.dishes.updated} · Option: +
                              {menuImportResult.options.added} / ~{menuImportResult.options.updated} · Quan hệ: +
                              {menuImportResult.relations.added} / ~{menuImportResult.relations.updated}
                            </Text>
                          </div>
                        }
                        warnings={menuImportResult.errors}
                      />
                    )}
                  </>
                ),
              },
              {
                key: 'invoice',
                label: 'Import Hóa đơn',
                children: (
                  <>
                    {invoiceParsed && (
                      <Row gutter={16} style={{ marginBottom: 16 }}>
                        <Col>
                          <Tag color="blue">{invoiceParsed.meta.totalInvoices} hóa đơn</Tag>
                        </Col>
                        <Col>
                          <Tag color="green">{invoiceParsed.meta.totalLineItems} dòng món</Tag>
                        </Col>
                        <Col>
                          <Tag color="purple">{invoiceParsed.meta.totalOptions} tùy chọn</Tag>
                        </Col>
                        {invoiceWarnings.length > 0 && (
                          <Col>
                            <Tag color="warning">{invoiceWarnings.length} cảnh báo map</Tag>
                          </Col>
                        )}
                      </Row>
                    )}

                    {invoiceWarnings.length > 0 && !invoiceImportResult && (
                      <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 12 }}
                        message="Tùy chọn chưa có trong catalog"
                        description={
                          <div>
                            <p style={{ margin: '0 0 8px' }}>
                              Khi lưu, hệ thống sẽ <strong>tự tạo option mới (giá 0)</strong> và hiển thị cảnh báo để
                              bạn rà soát sau trong Quản lý món / tùy chọn.
                            </p>
                            <ul style={{ margin: 0, paddingLeft: 18 }}>
                              {invoiceWarnings.slice(0, 3).map((w, i) => (
                                <li key={i}>{w}</li>
                              ))}
                              {invoiceWarnings.length > 3 ? (
                                <li>... và {invoiceWarnings.length - 3} cảnh báo khác</li>
                              ) : null}
                            </ul>
                          </div>
                        }
                      />
                    )}

                    {invoicePreview.truncated > 0 && (
                      <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 12 }}
                        message={`Hiển thị ${CUKCUK_PREVIEW_MAX_ROWS} / ${invoicePreview.rows.length + invoicePreview.truncated} dòng đầu tiên`}
                      />
                    )}

                    {invoiceWarnings.length > 0 && (
                      <div style={{ marginBottom: 12 }}>
                        <Button
                          size="small"
                          onClick={() =>
                            downloadWarningsCsv(
                              invoiceWarnings,
                              invoiceFile?.name || 'cukcuk-invoice-import',
                            )
                          }
                        >
                          Tải CSV cảnh báo ({invoiceWarnings.length})
                        </Button>
                      </div>
                    )}

                    <div className="cukcuk-summary-bar">
                      <div className="cukcuk-summary-item">
                        <Text type="secondary">Dòng hóa đơn</Text>
                        <strong>{invoiceAllRows.length}</strong>
                      </div>
                      <div className="cukcuk-summary-item">
                        <Text type="secondary">Hóa đơn</Text>
                        <strong>{new Set(invoiceAllRows.map((row) => row.invoiceCode)).size}</strong>
                      </div>
                      <div className="cukcuk-summary-item">
                        <Text type="secondary">Đã ánh xạ</Text>
                        <strong>{invoiceAllRows.filter((row) => row.optionCount > 0 && row.mappedCount === row.optionCount).length}</strong>
                      </div>
                      <Button type="text" icon={<ReloadOutlined />} onClick={() => void loadSavedInvoices()} loading={savedInvoiceLoading}>
                        Làm mới
                      </Button>
                    </div>

                    <div className="cukcuk-filter-bar">
                      <Input
                        allowClear
                        prefix={<SearchOutlined />}
                        placeholder="Tìm mã hóa đơn, món, bàn…"
                        value={invoiceSearch}
                        onChange={(event) => setInvoiceSearch(event.target.value)}
                      />
                      <Select
                        value={invoiceMapFilter}
                        onChange={setInvoiceMapFilter}
                        options={[
                          { value: 'all', label: 'Tất cả ánh xạ' },
                          { value: 'mapped', label: 'Đã ánh xạ đủ' },
                          { value: 'partial', label: 'Ánh xạ một phần' },
                          { value: 'unmapped', label: 'Chưa ánh xạ' },
                        ]}
                      />
                      <Select
                        showSearch
                        optionFilterProp="label"
                        value={invoiceTableFilter}
                        onChange={setInvoiceTableFilter}
                        options={[
                          { value: 'all', label: 'Tất cả bàn' },
                          ...invoiceTableOptions.map((table) => ({ value: table, label: table })),
                        ]}
                      />
                      <Input aria-label="Từ ngày" type="date" value={invoiceDateFrom} onChange={(event) => setInvoiceDateFrom(event.target.value)} />
                      <Input aria-label="Đến ngày" type="date" value={invoiceDateTo} onChange={(event) => setInvoiceDateTo(event.target.value)} />
                      <Button onClick={() => { setInvoiceSearch(''); setInvoiceMapFilter('all'); setInvoiceTableFilter('all'); setInvoiceDateFrom(''); setInvoiceDateTo(''); }}>Xóa lọc</Button>
                    </div>

                    <Table<InvoicePreviewRow>
                      columns={invoiceTableColumns}
                      dataSource={invoicePreview.rows}
                      loading={invoicePreviewLoading || savedInvoiceLoading}
                      size="small"
                      scroll={{ x: 1100 }}
                      pagination={{ pageSize: 15, showSizeChanger: true, showTotal: (t) => `${t} dòng` }}
                      locale={{
                        emptyText: invoiceAllRows.length ? (
                          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Không có hóa đơn phù hợp">
                            <Button onClick={() => { setInvoiceSearch(''); setInvoiceMapFilter('all'); setInvoiceTableFilter('all'); }}>Xóa bộ lọc</Button>
                          </Empty>
                        ) : (
                          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Chưa có hóa đơn CUKCUK">
                            <Button type="primary" onClick={() => setImportOpen(true)}>Nhập hóa đơn</Button>
                          </Empty>
                        ),
                      }}
                    />

                    {invoiceImportResult && (
                      <ImportResultPanel
                        title="Kết quả lưu Hóa đơn"
                        successCount={invoiceSuccessCount}
                        failCount={invoiceFailCount}
                        detail={
                          <div style={{ marginTop: 12 }}>
                            <Text>
                              {invoiceImportResult.lineItems} dòng món · {invoiceImportResult.options} tùy chọn ·
                              Thêm {invoiceImportResult.added} · Cập nhật {invoiceImportResult.updated}
                            </Text>
                            {(invoiceImportResult.autoCreatedOptions.added > 0 ||
                              invoiceImportResult.autoCreatedOptions.updated > 0) && (
                              <div style={{ marginTop: 8 }}>
                                <Tag color="processing">
                                  Option tự tạo: +{invoiceImportResult.autoCreatedOptions.added} / ~
                                  {invoiceImportResult.autoCreatedOptions.updated}
                                </Tag>
                                <Text type="secondary" style={{ marginLeft: 8 }}>
                                  Giá mặc định 0 — rà soát tại Quản lý món / tùy chọn.
                                </Text>
                              </div>
                            )}
                          </div>
                        }
                        warnings={[
                          ...invoiceWarnings,
                          ...invoiceImportResult.optionWarnings,
                          ...invoiceImportResult.errors,
                        ]}
                      />
                    )}
                  </>
                ),
              },
            ].filter((item) => item.key === view)}
          />
        </div>

      </AdminLayout>
    </ConfigProvider>
  );
};

export default CukcukImportPage;